import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { useMetroHealth, useBackfillMetroAssignments } from '@/hooks/useMetroHealth';

export function MetroHealthCard() {
  const { data: stats, isLoading, refetch } = useMetroHealth();
  const backfillMutation = useBackfillMetroAssignments();

  const getHealthColor = (missing: number, total: number) => {
    if (total === 0) return 'text-muted-foreground';
    const percentage = (missing / total) * 100;
    if (percentage > 50) return 'text-destructive';
    if (percentage > 20) return 'text-warning';
    return 'text-success';
  };

  const getPercentage = (missing: number, total: number) => {
    if (total === 0) return '0%';
    return `${((missing / total) * 100).toFixed(1)}%`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Metro Assignment Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const leadsOk = stats?.leads.missingMetro === 0;
  const jobsOk = stats?.jobs.missingMetro === 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Metro Assignment Health
            </CardTitle>
            <CardDescription>
              Postcode and metro assignment coverage
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => backfillMutation.mutate()}
              disabled={backfillMutation.isPending}
            >
              {backfillMutation.isPending ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Recalculate Metros
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Alert */}
        {(stats?.leads.missingMetro || 0) > 0 || (stats?.jobs.missingMetro || 0) > 0 ? (
          <div className="flex items-start gap-2 p-3 bg-warning/10 text-warning-foreground rounded-md border border-warning/20">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-warning" />
            <div className="text-sm">
              <p className="font-medium">
                {(stats?.leads.missingMetro || 0) + (stats?.jobs.missingMetro || 0)} records missing metro assignment
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Records without GPS coordinates or postcodes cannot be auto-assigned. Click "Recalculate Metros" to assign all that can be matched.
              </p>
            </div>
          </div>
        ) : null}

        {/* Leads Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">Leads</h4>
            <Badge variant={leadsOk ? 'default' : 'destructive'}>
              {leadsOk ? (
                <CheckCircle className="h-3 w-3 mr-1" />
              ) : (
                <AlertTriangle className="h-3 w-3 mr-1" />
              )}
              {stats?.leads.total || 0} total
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Missing Postcode:</span>
              <span className={getHealthColor(stats?.leads.missingPostcode || 0, stats?.leads.total || 0)}>
                {stats?.leads.missingPostcode || 0} ({getPercentage(stats?.leads.missingPostcode || 0, stats?.leads.total || 0)})
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Missing Metro:</span>
              <span className={getHealthColor(stats?.leads.missingMetro || 0, stats?.leads.total || 0)}>
                {stats?.leads.missingMetro || 0} ({getPercentage(stats?.leads.missingMetro || 0, stats?.leads.total || 0)})
              </span>
            </div>
          </div>
        </div>

        {/* Jobs Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">Jobs</h4>
            <Badge variant={jobsOk ? 'default' : 'destructive'}>
              {jobsOk ? (
                <CheckCircle className="h-3 w-3 mr-1" />
              ) : (
                <AlertTriangle className="h-3 w-3 mr-1" />
              )}
              {stats?.jobs.total || 0} total
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Missing Postcode:</span>
              <span className={getHealthColor(stats?.jobs.missingPostcode || 0, stats?.jobs.total || 0)}>
                {stats?.jobs.missingPostcode || 0} ({getPercentage(stats?.jobs.missingPostcode || 0, stats?.jobs.total || 0)})
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Missing Metro:</span>
              <span className={getHealthColor(stats?.jobs.missingMetro || 0, stats?.jobs.total || 0)}>
                {stats?.jobs.missingMetro || 0} ({getPercentage(stats?.jobs.missingMetro || 0, stats?.jobs.total || 0)})
              </span>
            </div>
          </div>
        </div>

        {(stats?.leads.missingPostcode || 0) > 0 || (stats?.leads.missingMetro || 0) > 0 ||
         (stats?.jobs.missingPostcode || 0) > 0 || (stats?.jobs.missingMetro || 0) > 0 ? (
          <p className="text-xs text-muted-foreground">
            Click "Recalculate Metros" to assign metros using GPS coordinates (Haversine distance to metro center), with postcode range as fallback for records without coordinates.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
